import ClearableTextField from '@/components/ClearableTextField';
import DropdownList from '@/components/DropdownList';
import LocationSearchAutocomplete from '@/components/LocationSearchAutocomplete';
import DatePickersSkeleton from '@/modules/JobSearchFilters/components/DatePickers/DatePickersSkeleton';
import MinPayRateDropdown from '@/modules/JobSearchFilters/components/MinPayRateDropdown/MinPayRateDropdown';
import MoreFiltersSection from '@/modules/JobSearchFilters/components/MoreFiltersSection/MoreFiltersSection';
import WorkTypeButtonGroup from '@/modules/JobSearchFilters/components/WorkTypeButtonGroup/WorkTypeButtonGroup';
import { ILocation, ISchedulerLocation } from '@/services/types';
import {
  DateSearch,
  JobRootSpecialtyV2,
  JobSearchGrade,
  JobSpeciality,
  JobSubSpeciality,
  MinPayRateUnit,
  WorkType,
} from '@/types/jobs';
import { clearScrollWhenSearchCriteriaHasChanged } from '@/utils/jobSearchAutoScroll';
import { getRadiusOptions } from '@/utils/jobSearchBoxOptions';
import cx from 'classnames';
import debounce from 'lodash.debounce';
import dynamic from 'next/dynamic';
import React, {
  ChangeEvent,
  ReactElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { Button, Collapse, FormControl, Theme, useMediaQuery } from '@material-ui/core';
import KeyboardArrowDownOutlinedIcon from '@material-ui/icons/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@material-ui/icons/KeyboardArrowUpOutlined';

import styles from './JobSearchFiltersForm.module.scss';

// @material-ui/pickers + @date-io/date-fns are a heavy dependency that only this one field
// needs, so it's split into its own chunk rather than shipping in the main filters bundle for
// every job search page load. ssr:false since it's a client-only refinement with no SEO/content
// value; DatePickersSkeleton reserves the same layout space to avoid a CLS pop-in once it loads.
const DatePickers = dynamic(
  () => import('@/modules/JobSearchFilters/components/DatePickers/DatePickers'),
  { ssr: false, loading: DatePickersSkeleton },
);

export type JobSearchFiltersFormProps = {
  date: DateSearch;
  onDateChange: (value: DateSearch) => void;
  grade?: string;
  onGradeChange: (value: JobSearchGrade) => void;
  speciality?: string;
  onSpecialityChange: (value: JobSpeciality) => void;
  rootSpeciality?: string;
  onRootSpecialityChange: (value: JobRootSpecialtyV2) => void;
  subspeciality?: string;
  onSubSpecialityChange: (value: JobSubSpeciality) => void;
  keyword?: string;
  onKeywordChange: (value: string) => void;
  workType?: WorkType;
  onWorkTypeChange: (value: WorkType | undefined) => void;
  locationCoordinate: ILocation | ISchedulerLocation | null;
  onLocationCoordinateChange: (value: ILocation | ISchedulerLocation | null) => void;
  radius?: string;
  onRadiusChange: (value: string) => void;
  minPayRate: string | null;
  onMinPayRateChange: (value: string | null) => void;
  minPayRateUnit: MinPayRateUnit;
  onMinPayRateUnitChange: (value: MinPayRateUnit) => void;
  isEndDateDisabled: boolean;
  isRadiusDisabled: boolean;
  onClear: () => void;
  onSearch: () => void;
  getGradeOptions: () => ReactElement[];
  getSpecialityOptions: () => ReactElement[];
  getRootSpecialityOptions: () => ReactElement[];
  getSubSpecialityOptions: () => ReactElement[];
  onOpenChange?: (isOpen: boolean) => void;
};

export type JobSearchFiltersFormHandle = {
  toggle: () => void;
};

const JobSearchFiltersForm = forwardRef<JobSearchFiltersFormHandle, JobSearchFiltersFormProps>(
  (
    {
      date,
      onDateChange,
      grade,
      onGradeChange,
      speciality,
      onSpecialityChange,
      keyword,
      onKeywordChange,
      workType,
      onWorkTypeChange,
      radius,
      onRadiusChange,
      locationCoordinate,
      onLocationCoordinateChange,
      minPayRate,
      onMinPayRateChange,
      minPayRateUnit,
      onMinPayRateUnitChange,
      isEndDateDisabled,
      isRadiusDisabled,
      onClear,
      onSearch,
      getGradeOptions,
      getSpecialityOptions,
      subspeciality,
      onSubSpecialityChange,
      getSubSpecialityOptions,
      rootSpeciality,
      onRootSpecialityChange,
      getRootSpecialityOptions,
      onOpenChange,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const isCollapsible = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    // by default, should be closed on mobile and opened otherwise
    useEffect(() => {
      setIsOpen(isCollapsible === false);
    }, [isCollapsible]);

    useEffect(() => {
      onOpenChange?.(isOpen);
    }, [isOpen]);

    const handleOpenClick = () => {
      if (isCollapsible) {
        setIsOpen(!isOpen);
      }
    };

    useImperativeHandle(ref, () => ({
      toggle: handleOpenClick,
    }));

    const handleKeywordChange = debounce(
      (e: ChangeEvent<HTMLInputElement>) => onKeywordChange(e.target.value),
      500,
    );

    const handleSearchClick = () => {
      onSearch();
      if (isCollapsible) {
        setIsOpen(false);
      }
    };

    return (
      <div
        data-testid={'job-filters-container'}
        className={cx(styles.container, !isOpen && styles.collapsed)}
      >
        <button
          className={styles.header}
          onClick={handleOpenClick}
          disabled={isCollapsible === false}
        >
          <span className={styles.filterIcon} aria-hidden="true">
            <span className={styles.filterIconBar} />
            <span className={styles.filterIconBar} />
            <span className={styles.filterIconBar} />
          </span>
          <span className={styles.header_text}>Filters</span>
          {isCollapsible && (
            <span className={styles.header_downIcon}>
              {isOpen ? <KeyboardArrowUpOutlinedIcon /> : <KeyboardArrowDownOutlinedIcon />}
            </span>
          )}
        </button>
        <Collapse in={isOpen} timeout="auto">
          <div className={styles.body}>
            <div className={cx(styles.row, styles.roleFilterRow)}>
              <div className={cx(styles.field, styles.dropdownField)}>
                <DropdownList
                  id="grade"
                  label="Grade"
                  value={grade}
                  testId="job-search-grade-input"
                  onChange={e => {
                    onGradeChange(('' + e.target.value) as JobSearchGrade);
                    clearScrollWhenSearchCriteriaHasChanged();
                  }}
                  renderOptions={getGradeOptions}
                />
              </div>

              <div className={cx(styles.field, styles.dropdownField)}>
                <DropdownList
                  id="rootSpeciality"
                  label="Speciality"
                  value={rootSpeciality}
                  testId="job-search-root-speciality-input"
                  onChange={e => {
                    onRootSpecialityChange(('' + e.target.value) as JobRootSpecialtyV2);
                    clearScrollWhenSearchCriteriaHasChanged();
                  }}
                  renderOptions={getRootSpecialityOptions}
                />
              </div>

              <div className={cx(styles.field, styles.dropdownField)}>
                <DropdownList
                  id="subspeciality"
                  label="Subspeciality"
                  value={subspeciality}
                  testId="job-search-sub-speciality-input"
                  onChange={e => {
                    onSubSpecialityChange(('' + e.target.value) as JobSubSpeciality);
                    clearScrollWhenSearchCriteriaHasChanged();
                  }}
                  renderOptions={getSubSpecialityOptions}
                />
              </div>
            </div>

            <div className={cx(styles.row, styles.workType)}>
              <WorkTypeButtonGroup
                selectedWorkType={workType}
                onWorkTypeChange={workType => {
                  onWorkTypeChange(workType);
                  clearScrollWhenSearchCriteriaHasChanged();
                }}
              />
            </div>

            <div className={cx(styles.row, styles.locationRadiusRow)}>
              <div className={styles.locationField}>
                <LocationSearchAutocomplete
                  selectedLocation={locationCoordinate}
                  onSelectedLocationChange={onLocationCoordinateChange}
                />
              </div>
              <span className={styles.divider} aria-hidden="true" />
              <div className={styles.radiusField}>
                {!radius && <span className={styles.radiusPlaceholder}>Radius</span>}
                <DropdownList
                  id="radius"
                  label="Radius"
                  value={radius}
                  testId="job-search-radius-input"
                  disabled={isRadiusDisabled}
                  tooltipMessage={
                    isRadiusDisabled
                      ? 'Radius is available when a specific city or town is selected.'
                      : undefined
                  }
                  onChange={e => {
                    onRadiusChange(e.target.value);
                    clearScrollWhenSearchCriteriaHasChanged();
                  }}
                  renderOptions={() => getRadiusOptions()}
                />
              </div>
            </div>

            <div className={cx(styles.row, styles.roleFilterRow)}>
              <MoreFiltersSection count={3}>
                <div className={cx(styles.datesRow, styles.formFieldsRow)}>
                  <DatePickers
                    date={date}
                    onDateChange={onDateChange}
                    isEndDateDisabled={isEndDateDisabled}
                  />
                </div>

                <div className={cx(styles.keywordsRow, styles.formFieldsRow)}>
                  <FormControl fullWidth>
                    <ClearableTextField
                      id="keywords"
                      name="keywords"
                      placeholder="Keywords"
                      label={'Keywords'}
                      defaultValue={keyword ?? ''}
                      onChange={handleKeywordChange}
                      inputProps={{ 'data-testid': 'job-search-keywords-input' }}
                      InputLabelProps={{ shrink: false }}
                    />
                  </FormControl>
                </div>

                <div className={styles.minPayRateRow}>
                  <MinPayRateDropdown
                    minPayRate={minPayRate}
                    onMinPayRateChange={onMinPayRateChange}
                    minPayRateUnit={minPayRateUnit}
                    onMinPayRateUnitChange={onMinPayRateUnitChange}
                  />
                </div>
              </MoreFiltersSection>
            </div>

            <div className={cx(styles.row, styles.actions)}>
              <Button
                type="button"
                onClick={handleSearchClick}
                color="primary"
                size="medium"
                variant="contained"
                fullWidth
                data-testid="job-search-search"
                className={styles.searchButton}
              >
                Search
              </Button>
            </div>

            <div className={cx(styles.row, styles.clear)}>
              <Button
                type="button"
                onClick={onClear}
                color="primary"
                size="medium"
                variant="text"
                data-testid="job-search-clear"
                className={styles.clearButton}
              >
                Clear all
              </Button>
            </div>
          </div>
        </Collapse>
      </div>
    );
  },
);

JobSearchFiltersForm.displayName = 'JobSearchFiltersForm';

export default JobSearchFiltersForm;
